import React from "react";
import ComponentAbstract from "./ComponentAbstract";
import _request from "actions/_request";
import i18n from "i18";
import {
  Box,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";

export default class TelegramChatSelector extends ComponentAbstract {
  constructor(props) {
    super(props);

    this.state = {
      step: "token",
      botToken: "",
      selectedChatId: "",
      chatsData: null,
      isLoadingChats: false,
      isConnecting: false,
      error: null,
      guideExpanded: true,
    };

    this.handleGetChats = this.handleGetChats.bind(this);
    this.handleConnect = this.handleConnect.bind(this);
    this.handleBack = this.handleBack.bind(this);
  }

  async handleGetChats() {
    const { botToken } = this.state;

    if (!botToken.trim()) {
      this.changeState("error", i18n.t("accountsPage.telegramTokenRequired"));
      return;
    }

    this.changeState("isLoadingChats", true);
    this.changeState("error", null);

    const response = await _request.accounts.getTelegramChats(botToken);

    this.changeState("isLoadingChats", false);

    if (response.isOk && response.data) {
      this.changeState("chatsData", response.data);
      this.changeState("step", "select");
    } else {
      const errorMessage =
        response.data || response.error || i18n.t("accountsPage.invalidBotToken");
      this.changeState("error", errorMessage);
    }
  }

  async handleConnect() {
    const { botToken, selectedChatId } = this.state;
    const { onSuccess } = this.props;

    if (!selectedChatId) {
      this.changeState("error", i18n.t("accountsPage.selectChatRequired"));
      return;
    }

    this.changeState("isConnecting", true);
    this.changeState("error", null);

    const response = await _request.accounts.addTelegramAccount(
      botToken,
      selectedChatId,
    );

    this.changeState("isConnecting", false);

    if (response.isOk) {
      if (onSuccess) {
        onSuccess();
      }
    } else {
      const errorMessage =
        response.data ||
        response.error ||
        i18n.t("accountsPage.connectionError");
      this.changeState("error", errorMessage);
    }
  }

  handleBack() {
    this.changeState("step", "token");
    this.changeState("selectedChatId", "");
    this.changeState("error", null);
  }

  renderSetupGuide() {
    const { guideExpanded } = this.state;

    const steps = [
      {
        num: 1,
        titleKey: "telegramSetupStep1Title",
        descKey: "telegramSetupStep1Desc",
        Icon: SmartToyOutlinedIcon,
      },
      {
        num: 2,
        titleKey: "telegramSetupStep2Title",
        descKey: "telegramSetupStep2Desc",
        Icon: AdminPanelSettingsOutlinedIcon,
      },
      {
        num: 3,
        titleKey: "telegramSetupStep3Title",
        descKey: "telegramSetupStep3Desc",
        Icon: LinkOutlinedIcon,
      },
    ];

    return (
      <Accordion
        expanded={guideExpanded}
        onChange={() => this.changeState("guideExpanded", !guideExpanded)}
        disableGutters
        sx={{
          backgroundColor: "rgba(42, 171, 238, 0.06)",
          border: "1px solid rgba(42, 171, 238, 0.25)",
          borderRadius: "10px !important",
          boxShadow: "none",
          "&:before": { display: "none" },
        }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#2AABEE" }} />}
          sx={{
            minHeight: "44px",
            padding: "0 14px",
            "& .MuiAccordionSummary-content": { margin: "10px 0" },
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              component="span"
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#2AABEE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
              <Typography sx={{ color: "#fff", fontSize: "11px", fontWeight: 700, lineHeight: 1 }}>?</Typography>
            </Box>
            <Typography className="font w500 f14" sx={{ color: "#2AABEE" }}>
              {i18n.t("accountsPage.telegramSetupGuide")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: "0 14px 14px" }}>
          <Divider sx={{ borderColor: "rgba(42, 171, 238, 0.2)", marginBottom: "14px" }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {steps.map(({ num, titleKey, descKey, Icon }) => (
              <Box key={num} sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    minWidth: 28,
                    borderRadius: "50%",
                    backgroundColor: "#2AABEE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "1px",
                  }}>
                  <Typography sx={{ color: "#fff", fontSize: "12px", fontWeight: 700, lineHeight: 1 }}>
                    {num}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Icon sx={{ fontSize: "15px", color: "var(--text-color)", opacity: 0.7 }} />
                    <Typography className="font w600 f13" sx={{ color: "var(--text-color)" }}>
                      {i18n.t(`accountsPage.${titleKey}`)}
                    </Typography>
                  </Box>
                  <Typography
                    className="font f12"
                    sx={{ color: "var(--text-secondary)", opacity: 0.85, lineHeight: 1.55, paddingLeft: "21px" }}>
                    {i18n.t(`accountsPage.${descKey}`)}
                  </Typography>
                </Box>
              </Box>
            ))}

            <Box
              sx={{
                marginTop: "2px",
                padding: "8px 12px",
                backgroundColor: "rgba(42, 171, 238, 0.1)",
                borderRadius: "7px",
                border: "1px solid rgba(42, 171, 238, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
              <Typography className="font f12" sx={{ color: "var(--text-secondary)" }}>
                {i18n.t("accountsPage.telegramSetupTip")}{" "}
                <a
                  href="https://t.me/BotFather"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#2AABEE", textDecoration: "none", fontWeight: 600 }}>
                  @BotFather
                </a>
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  renderTokenStep() {
    const { botToken, isLoadingChats, error } = this.state;
    const { onCancel } = this.props;

    return (
      <Box className="flex column gap16">
        {this.renderSetupGuide()}

        <Typography
          className="font w500 f16"
          sx={{ color: "var(--text-color)" }}>
          {i18n.t("accountsPage.telegramBotToken")}
        </Typography>

        {this.insertGlobalInput({
          value: botToken,
          onChange: (e) => this.changeState("botToken", e.target.value),
          placeholder: "123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11",
          disabled: isLoadingChats,
          size: "small",
          sx: { width: "100%" },
          InputProps: {
            sx: {
              color: "var(--input-text)",
              backgroundColor: "var(--input-bg)",
              border: error ? "1px solid #f44336" : "1px solid var(--border-color)",
              borderRadius: "7px",
              width: "100%",
            },
          },
        })}

        {error && (
          <Alert severity="error" sx={{ fontSize: "14px" }}>
            {error}
          </Alert>
        )}

        <Typography
          className="font f14"
          sx={{ color: "var(--text-secondary)", opacity: 0.7 }}>
          {i18n.t("accountsPage.telegramTokenHint")}
        </Typography>

        <Box className="flex gap16">
          <Button
            variant="contained"
            className="btnDark"
            disabled={!botToken.trim() || isLoadingChats}
            onClick={this.handleGetChats}>
            {isLoadingChats && (
              <CircularProgress
                size={16}
                sx={{ marginRight: "8px", color: "var(--btn-color)" }}
              />
            )}
            {i18n.t("accountsPage.getAvailableChats")}
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ color: "var(--text-color)", borderColor: "var(--border-color)" }}>
            {i18n.t("cancel")}
          </Button>
        </Box>
      </Box>
    );
  }

  renderChatSelection() {
    const { chatsData, selectedChatId, isConnecting, error } = this.state;

    const chats = chatsData?.chats || [];
    const bot = chatsData?.bot;

    return (
      <Box className="flex column gap16">
        <Typography
          className="font w500 f16"
          sx={{ color: "var(--text-color)" }}>
          {i18n.t("accountsPage.selectChannel")}
        </Typography>

        {bot && (
          <Typography
            className="font f14"
            sx={{ color: "var(--text-secondary)" }}>
            {i18n.t("accountsPage.bot")}: @{bot.username}
          </Typography>
        )}

        {chats.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: "14px" }}>
            {chatsData?.message || i18n.t("accountsPage.noChatsFound")}
          </Alert>
        ) : (
          <RadioGroup
            value={selectedChatId}
            onChange={(e) => this.changeState("selectedChatId", e.target.value)}>
            {chats.map((chat) => (
              <Box
                key={chat.chat_id}
                className="flex gap8"
                sx={{
                  padding: "12px",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  marginBottom: "8px",
                  backgroundColor: chat.bot_can_post
                    ? "var(--background-image)"
                    : "var(--hover-bg)",
                  opacity: chat.bot_can_post ? 1 : 0.5,
                }}>
                <FormControlLabel
                  value={chat.chat_id}
                  control={<Radio disabled={!chat.bot_can_post} />}
                  disabled={!chat.bot_can_post}
                  label={
                    <Box className="flex column gap4">
                      <Typography
                        className="font w500 f14"
                        sx={{ color: "var(--text-color)" }}>
                        {chat.type === "channel" ? "📢" : "👥"} {chat.title}
                      </Typography>
                      <Typography
                        className="font f12"
                        sx={{ color: "var(--text-secondary)" }}>
                        {chat.username || i18n.t("accountsPage.noUsername")} ·{" "}
                        {chat.member_count
                          ? `${chat.member_count} ${i18n.t("accountsPage.members")}`
                          : i18n.t("accountsPage.private")}
                      </Typography>
                      <Box className="flex gap4" sx={{ alignItems: "center" }}>
                        {chat.bot_can_post ? (
                          <>
                            <CheckCircleIcon
                              style={{ fontSize: "14px", color: "#4caf50" }}
                            />
                            <Typography
                              className="font f12"
                              sx={{ color: "#4caf50" }}>
                              {i18n.t("accountsPage.botCanPost")}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <ErrorIcon
                              style={{ fontSize: "14px", color: "#ff9800" }}
                            />
                            <Typography
                              className="font f12"
                              sx={{ color: "#ff9800" }}>
                              {i18n.t("accountsPage.botCannotPost")}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  }
                  sx={{ margin: 0, width: "100%" }}
                />
              </Box>
            ))}
          </RadioGroup>
        )}

        {error && (
          <Alert severity="error" sx={{ fontSize: "14px" }}>
            {error}
          </Alert>
        )}

        <Box className="flex gap16">
          <Button
            variant="contained"
            className="btnDark"
            disabled={!selectedChatId || isConnecting}
            onClick={this.handleConnect}>
            {isConnecting && (
              <CircularProgress
                size={16}
                sx={{ marginRight: "8px", color: "var(--btn-color)" }}
              />
            )}
            {i18n.t("accountsPage.connectSelected")}
          </Button>
          <Button
            variant="outlined"
            onClick={this.handleBack}
            sx={{ color: "var(--text-color)", borderColor: "var(--border-color)" }}>
            {i18n.t("accountsPage.back")}
          </Button>
        </Box>
      </Box>
    );
  }

  render() {
    const { step } = this.state;

    return (
      <Box className="telegram-selector" sx={{ width: "100%", minWidth: { xs: "unset", sm: "450px" }, maxWidth: "560px" }}>
        {step === "token" ? this.renderTokenStep() : this.renderChatSelection()}
      </Box>
    );
  }
}
